<template>
	<v-dialog
		v-model="dialog"
		width="1080px"
		persistent>
		<v-card class="dir-listing-card">

			<v-card-title class="background--text text--lighten-5 pa-2">
				<v-icon
					large
					class="mr-2"
					color="background lighten-5">
					find_in_page
				</v-icon>
				<span class="headline mr-2">Export CSV</span>
				{{ root_path }}
				<v-spacer></v-spacer>
				<v-btn
					icon flat
					:disabled="exporting"
					@click="close()">
					<v-icon>close</v-icon>
				</v-btn>
			</v-card-title>
			<v-divider class="ma-0"></v-divider>

			<v-card-text>
				<v-layout
					align-center
					fill-height
					class="report-card-container">
					<div class="report-card">
						<div>
							<v-icon large>folder_open</v-icon>
						</div>
						<div class="headline">
							{{ progress.foldersFound }}
						</div>
						<div class="caption">
							Dossiers
						</div>
					</div>

					<div class="report-card">
						<div>
							<v-icon large>insert_drive_file</v-icon>
						</div>
						<div class="headline">
							{{ progress.filesFound }}
						</div>
						<div class="caption">
							Fichiers
						</div>
					</div>

					<div
						class="report-card"
						:class="progress.entriesFound >= max_rows ?
						'orange--text text--accent-3' :
						'green--text text--accent-3'">
						<div>
							<v-icon
								large
								:color="progress.entriesFound >= max_rows ?
									'orange accent-3' : 'green accent-3'">
								format_list_numbered
							</v-icon>
						</div>
						<div>
							<span class="headline">
								{{ progress.entriesFound }}
							</span>
							<span class="body-1">
								/ {{ max_rows }}
							</span>
						</div>
						<div class="caption">
							Lignes du CSV
						</div>
					</div>

					<div class="report-card">
						<div>
							<v-icon
								v-if="progress.errors > 0"
								large
								color="red accent-2">
								dangerous
							</v-icon>
							<v-icon
								v-else
								large
								color="green accent-3">
								check
							</v-icon>
						</div>
						<div :class="progress.errors > 0 ?
							'red--text text--accent-2' :
							'green--text text--accent-3'"
							class="headline">
							{{ progress.errors }}
						</div>
						<div :class="progress.errors > 0 ?
							'red--text text--accent-2' :
							'green--text text--accent-3'"
							class="caption">
							Erreur(s)
						</div>
					</div>
				</v-layout>

				<div class="current-dir-analysis" v-if="exporting">
					<div class="caption background--text text--lighten-5">
						Dossier en cours d'analyse :
					</div>
					<div class="current-dir-analysis-path">
						<v-progress-circular
							indeterminate
							width="2"
							color="white">
						</v-progress-circular>
						<span class="current-dir-analysis-path-text">
							{{ progress.currentPath }}
						</span>
					</div>
				</div>

				<v-alert
					v-if="progress.truncated"
					:value="true"
					color="warning"
					icon="error_outline"
					class="py-2 mt-3 truncated-alert"
					outline>
					<div class="subheading font-weight-bold">
						Limite atteinte
					</div>
					La limite des {{ max_rows }} lignes a été atteinte.
					Le CSV est tronqué.
				</v-alert>
			</v-card-text>

			<v-card-actions v-if="exporting">
				<v-spacer></v-spacer>
				<v-btn
					color="red accent-2"
					flat
					@click="cancelExport()">
					<v-icon class="mr-2">stop_circle</v-icon>
					Stop
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
const MAX_ROWS = 10000
const getInitialProgress = () => ({
	foldersScanned: 0,
	entriesFound: 0,
	filesFound: 0,
	foldersFound: 0,
	currentPath: '',
	errors: 0,
	cancelled: false,
	truncated: false
})

export default {
	props: [ 'root_path', 'deep' ],
	data () {
		return {
			dialog: false,
			exporting: false,
			controller: null,
			max_rows: MAX_ROWS,
			progress: getInitialProgress()
		}
	},
	watch: {
		root_path (val) {
			if (val) this.exportTree()
		}
	},
	methods: {
		cancelExport () {
			if (this.controller) this.controller.abort()
		},
		close () {
			this.dialog = false
			this.exporting = false
			this.controller = null
			this.progress = getInitialProgress()
			this.$emit('close')
		},
		async exportTree () {
			this.dialog = true
			this.exporting = true
			this.controller = new AbortController()
			try {
				const {
					csv,
					cancelled,
					truncated
				} = await this.dir_listing(this.root_path, {
					maxDepth: this.deep ? Infinity : 1,
					onProgress: (p) => { this.progress = p },
					signal: this.controller.signal
				})

				if (cancelled) {
					this.close()
					return
				}

				const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				let csvName = this.root_path
					.replaceAll('/', '_')
					.replace('_media_DATA_', '')

				if (this.deep) csvName += '_FULL'
				if (truncated) csvName += '_TRUNCATED'
				csvName += '.csv'
				a.download = csvName
				a.click()
				URL.revokeObjectURL(url)
				this.$Toaster({
					title: `Export CSV`,
					content: `L'export CSV a été généré avec succès.`,
					type: 'success',
					loading: false,
					timeout: 2000
				})
			} finally {
				this.exporting = false
				this.controller = null
			}
		},
		async dir_listing (rootPath, options = {}) {
			const csvEscape = (value) => {
				if (value === null || value === undefined) return ''
				const str = String(value)
				if (/[",;\n]/.test(str)) {
					return '"' + str.replace(/"/g, '""') + '"'
				}
				return str
			}

			const {
				maxDepth = Infinity,
				onProgress = null,
				signal = null
			} = options

			let rows = []

			const stats = {
				foldersScanned: 0,
				entriesFound: 0,
				filesFound: 0,
				foldersFound: 0,
				currentPath: rootPath,
				errors: 0,
				cancelled: false,
				truncated: false
			}

			// let emitCounter = 0
			const emitProgress = (force = false) => {
				// emitCounter++
				// if (!force && emitCounter % 10 !== 0) return
				if (onProgress) onProgress({ ...stats })
			}

			const checkCancelled = () => {
				return signal && signal.aborted
			}

			const byteSizeToGB = (size) => {
				return parseFloat((size / Math.pow(1024, 3)).toFixed(2))
			}

			const walk = async (path, depth) => {
				if (checkCancelled()) return
				if (stats.entriesFound >= MAX_ROWS) return
				if (depth > maxDepth) return

				stats.currentPath = path.replace(rootPath, '.')
				emitProgress()

				let entries
				try {
					if (depth === 0) {
						let res = await this.$ApiNexus.stat(path)
						entries = [res.data]
					} else {
						let res = await this.$ApiNexus.ls(path, true, signal)
						entries = res.data
					}
				} catch (e) {
					if (e.name === 'AbortError') {
						return
					}
					console.error(`Erreur lors du listing de ${path}`, e)
					stats.errors++
					emitProgress()
					return
				}

				if (checkCancelled()) return

				stats.foldersScanned++
				stats.entriesFound += entries.length

				for (const entry of entries) {
					if (entry.type === 'file') stats.filesFound++
					if (entry.type === 'folder') stats.foldersFound++
					rows.push({
						type: entry.type,
						mod_date: entry.mod_date,
						size_go: byteSizeToGB(entry.size),
						size_octet: entry.size,
						path: entry.path.replace(rootPath, '.').replaceAll('/', '\\'),
						full_path: this.$paths.maestroToUNC(entry.path).replaceAll('/', '\\'),
						ext: entry.ext,
						// number_of_files: entry.number_of_files,
						depth: depth
					})
				}

				emitProgress()

				for (const entry of entries) {
					if (checkCancelled()) break
					if (stats.entriesFound >= MAX_ROWS) {
						stats.truncated = true
						break
					}
					if (entry.type === 'folder') {
						await walk(entry.path, depth + 1)
					}
				}
			}

			stats.entriesFound += 1
			stats.foldersFound += 1
			await walk(rootPath, 0)

			stats.cancelled = !!checkCancelled()
			emitProgress(true)

			const rootSize = rows.reduce((acc, row) => {
				const size = parseInt(row.size_octet, 10)
				if (!isNaN(size)) {
					if (row.type === 'file') acc += size
					if (row.type === 'folder' && !this.deep) acc += size
				}
				return acc
			}, 0)

			if (rows.length > 0) {
				rows[0].size_octet = rootSize
				rows[0].size_go = byteSizeToGB(rootSize)
			}

			rows = rows.sort((a, b) => {
				if (a.depth !== b.depth) return a.depth - b.depth
				return a.path.localeCompare(b.path)
			})

			const header = [
				'Type',
				'Modification_date',
				'size_go',
				'Size_octet',
				'Path',
				'Full_path',
				'Ext',
				// 'Nb_files',
				'depth'
			]

			let csvRows = []
			csvRows.push(header.join(';'))

			for (const row of rows) {
				csvRows.push([
					row.type,
					row.mod_date,
					row.size_go,
					row.size_octet,
					row.path,
					row.full_path,
					row.ext,
					// row.number_of_files || '',
					row.depth
				].map(csvEscape).join(';'))
			}

			return {
				csv: csvRows.join('\n'),
				cancelled: stats.cancelled,
				truncated: stats.truncated
			}
		}
	}
}
</script>

<style lang="stylus" scoped>
.dir-listing-card
	background: var(--v-background-darken1)
	border-radius: 6px
	border: 1px solid var(--v-background-lighten1)

.report-card-container
	gap: 18px
	.report-card
		background: #FFFFFF11
		border-radius: 6px
		border: 0.5px solid var(--v-background-lighten1)
		padding: 12px 32px
		text-align: center

.current-dir-analysis
	margin-top: 24px
	.current-dir-analysis-path
		min-height: 59px
		margin-top: 4px
		font-family: monospace
		background: #00000011
		border-radius: 6px
		border: 0.5px solid var(--v-background-lighten1)
		padding: 8px
		display: flex
		align-items: flex-start
		gap: 8px
		.v-progress-circular
			flex-shrink: 0
			align-self: center
		.current-dir-analysis-path-text
			word-break: break-all
			min-width: 0
			align-self: center

.truncated-alert
	border-radius: 6px
	background: #fb8c0017
</style>



// WEBPACK FOOTER //
// src/components/directory_list_csv_export.vue